// Durable record of the credential the user last chose during onboarding.
//
// The selection otherwise lives only in the practice URL's query string
// (`?vertical=cdl&jurisdiction=CA`). Every auth path — email sign-up (with
// its email-verification inbox round-trip), magic link, and OAuth — bounces
// the user through a callback that drops those params, so a freshly
// signed-up user lands back on a bare credential picker having "lost" the
// cert they started with. Persisting the selection lets the root dispatcher
// (app/index.tsx) restore it after auth.
//
// Storage strategy is platform-split (mirrors theme.tsx's web-sync /
// native-async pattern):
//   - Web: BetterAuth/OAuth redirects are full-page navigations that wipe
//     in-memory JS state, so we MUST persist to localStorage (sync read).
//   - Native: the in-memory `memo` covers the within-session auth bounce
//     (the app instance survives the external-browser round-trip). For
//     durability across a cold start we *also* write-through to
//     AsyncStorage and hydrate `memo` from it asynchronously on boot —
//     AsyncStorage is already a dependency, and the sync `getStoredSelection`
//     read stays sync by serving `memo` (hydrated by the time the post-auth
//     dispatcher reads it). SecureStore is unnecessary: the selection is a
//     credential *type*, not a secret.

import AsyncStorage from "@react-native-async-storage/async-storage";

export interface StoredSelection {
  verticalSlug: string;
  jurisdictionCode: string | null;
}

const KEY = "passfirst.selectedVertical";

let memo: StoredSelection | null = null;

function webStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    // Accessing localStorage can throw (privacy mode, sandboxed iframe).
    return null;
  }
}

function parseSelection(raw: string | null): StoredSelection | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSelection>;
    if (typeof parsed.verticalSlug === "string") {
      return {
        verticalSlug: parsed.verticalSlug,
        jurisdictionCode:
          typeof parsed.jurisdictionCode === "string"
            ? parsed.jurisdictionCode
            : null,
      };
    }
  } catch {
    // ignore malformed payloads
  }
  return null;
}

// Native cold-start hydration: pull the last selection out of AsyncStorage
// into the in-memory `memo` so a later synchronous read sees it. Web already
// reads localStorage synchronously, so this is a no-op there. Safe to await;
// also fired (fire-and-forget) on module load so it's usually ready by the
// time the post-auth dispatcher reads.
export async function hydrateStoredSelection(): Promise<void> {
  if (webStorage()) return;
  try {
    const fromDisk = parseSelection(await AsyncStorage.getItem(KEY));
    // Don't clobber a fresher in-session selection with the stored one.
    if (fromDisk && !memo) memo = fromDisk;
  } catch {
    // AsyncStorage unavailable — in-memory cache still covers the session.
  }
}

void hydrateStoredSelection();

export function setStoredSelection(sel: StoredSelection): void {
  memo = sel;
  const store = webStorage();
  if (store) {
    try {
      store.setItem(KEY, JSON.stringify(sel));
    } catch {
      // Quota / disabled storage — the in-memory copy still covers the
      // common same-instance case.
    }
    return;
  }
  // Native: fire-and-forget durable write. `memo` already updated, so a
  // failed write just means the choice doesn't survive a cold start.
  void AsyncStorage.setItem(KEY, JSON.stringify(sel)).catch(() => {});
}

export function getStoredSelection(): StoredSelection | null {
  const store = webStorage();
  if (store) {
    try {
      const fromDisk = parseSelection(store.getItem(KEY));
      if (fromDisk) return fromDisk;
    } catch {
      // Fall through to the in-memory copy.
    }
  }
  // Native (and web-storage-failure): the in-memory copy, hydrated from
  // AsyncStorage on boot via hydrateStoredSelection().
  return memo;
}

export function clearStoredSelection(): void {
  memo = null;
  const store = webStorage();
  if (store) {
    try {
      store.removeItem(KEY);
    } catch {
      // ignore
    }
    return;
  }
  void AsyncStorage.removeItem(KEY).catch(() => {});
}
